// Sign-in with the 3Seven tenant (SEC1). Tokens live in sessionStorage, which empties when the tab
// closes. No pay data is ever written to browser storage (SEC9): only MSAL's own token cache.
//
// Inside Teams the sign-in page can't load in Teams' frame, so the app asks Teams for the token
// instead (Teams SSO). It is a token for the same app registration and scope, so the API checks it
// exactly as it checks the browser's. The Teams manifest opens the app with ?inTeams=1.

import { InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from "@azure/msal-browser";
import { appConfig, MOCK } from "./config";

export const msal = new PublicClientApplication({
  auth: {
    clientId: appConfig.clientId,
    authority: `https://login.microsoftonline.com/${appConfig.tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: { cacheLocation: "sessionStorage" },
});

const scopes = [`api://${appConfig.clientId}/access_as_user`];

/** True when Teams opened the app (its manifest adds ?inTeams=1 to the tab's URL). */
export const IN_TEAMS = !MOCK && new URLSearchParams(window.location.search).has("inTeams");

const teams = () => import("@microsoft/teams-js");

/** The name and sign-in name from a token, so Teams needs no separate profile call. */
function accountFromToken(token: string): AccountInfo {
  const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
  const claims = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(part), (c) => c.charCodeAt(0)))) as { name?: string; preferred_username?: string; oid?: string; tid?: string };
  return { name: claims.name, username: claims.preferred_username ?? "", homeAccountId: claims.oid ?? "", localAccountId: claims.oid ?? "", tenantId: claims.tid ?? "", environment: "teams" } as AccountInfo;
}

/** Completes a redirect sign-in if one is in progress, and returns the signed-in account, if any. */
export async function initAuth(): Promise<AccountInfo | null> {
  if (MOCK) return { name: "Synthetic user (mock mode)", username: "mock@example.test" } as AccountInfo;
  if (IN_TEAMS) {
    const { app } = await teams();
    await app.initialize();
    return accountFromToken(await getToken());
  }
  await msal.initialize();
  const result = await msal.handleRedirectPromise();
  const account = result?.account ?? msal.getAllAccounts()[0] ?? null;
  if (account) msal.setActiveAccount(account);
  return account;
}

// In Teams, Teams holds the session: "signing in" again is a reload, which gets a fresh token silently.
export const signIn = () => (IN_TEAMS ? Promise.resolve(window.location.reload()) : msal.loginRedirect({ scopes }));
export const signOut = () => (MOCK ? Promise.resolve(window.location.reload()) : msal.logoutRedirect());

export async function getToken(): Promise<string> {
  if (IN_TEAMS) {
    const { authentication } = await teams();
    try {
      // Teams caches the token and renews it before it expires.
      return await authentication.getAuthToken();
    } catch (err) {
      throw new Error(`Teams couldn't sign you in to Base Camp (${err instanceof Error ? err.message : String(err)}). Try opening it in a browser.`);
    }
  }
  const account = msal.getActiveAccount();
  if (!account) throw new Error("Not signed in");
  try {
    return (await msal.acquireTokenSilent({ scopes, account })).accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) await msal.acquireTokenRedirect({ scopes, account });
    throw err;
  }
}
